import react, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllProducts } from "../features/products/productsSlice";
import { addItemToCart } from "../features/cart/cartSlice.js";
import GeneralProductCard from "../components/common/cards/GeneralProductCard.jsx";
import { Search } from "lucide-react";
import { Helmet } from "react-helmet";

const CATEGORIES = [
  { value: "", label: "Alle" },
  { value: "feuerloescher", label: "Feuerlöscher" },
  { value: "brandschutz", label: "Brandschutz" },
];

export default function ProduktListe() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { items, status } = useSelector((state) => state.products);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("standard");

  useEffect(() => {
    setLoading(true);
    if (items.length === 0) {
      dispatch(fetchAllProducts());
    }
    setLoading(false);
  }, [dispatch, items.length]);

  const handleClick = (id, kategorie) => {
    navigate(`/produkt/${id}?kategorie=${kategorie}`);
  };

  const handleAddToCart = (item) => {
    setLoading(true);
    if (!user) {
      toast.error("Sie müssen angemeldet sein!");
      navigate("/login");
    } else {
      dispatch(
        addItemToCart({
          user_id: user.idUser,
          product: item,
          menge: 1,
        }),
      );

      toast.success("Wurde zum Warenkorb hinzugefügt");
      setLoading(false);
    }
  };

  const activeCategoryLabel =
    CATEGORIES.find((c) => c.value === selected)?.label ?? "Alle";

  return (
    <div className="kn-page">
      <Helmet>
        <title>Brandschutzprodukte & Zubehör | Kaminfeger Knapp Buchs</title>
        <meta
          name="description"
          content="Feuerlöscher, Rauchmelder, CO-Melder und weiteres Brandschutz-Zubehör von Kaminfeger Knapp in Buchs. Passende Produkte für Ihr Objekt – kompetent beraten."
        />
      </Helmet>

      {loading && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="mx-2 mt-1 h-[2px] bg-gray-300 opacity-80 rounded-full animate-pulse" />
        </div>
      )}

      {/* Titelblock */}
      <div className="px-5 sm:px-8 lg:px-12 pt-12 pb-7">
        <div className="kn-eyebrow mb-3.5">Shop</div>
        <h1 className="kn-h1 max-w-[11em]">Alle Produkte auf einen Blick</h1>
      </div>

      {/* Suche + Kategorien */}
      <div className="flex flex-wrap items-center gap-4 px-5 sm:px-8 lg:px-12 pb-5 border-b border-knapp-border">
        <label className="flex items-center gap-2.5 border border-knapp-borderInput rounded-full px-4 py-2.5 flex-1 min-w-[240px] max-w-[420px] focus-within:border-knapp-ink transition-colors">
          <Search className="h-5 w-5 text-knapp-placeholder shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Produkt suchen…"
            aria-label="Produkt suchen"
            className="border-0 outline-none font-barlow text-base flex-1 min-w-0 bg-transparent placeholder:text-knapp-placeholder"
          />
        </label>

        <div className="flex gap-2 flex-wrap sm:ml-auto">
          {CATEGORIES.map((cat) => {
            const active = selected === cat.value;
            return (
              <button
                key={cat.value || "all"}
                type="button"
                onClick={() => setSelected(cat.value)}
                aria-pressed={active}
                className={
                  "rounded-full px-4 py-2 text-sm font-semibold transition-colors " +
                  (active
                    ? "bg-knapp-ink text-white border border-knapp-ink"
                    : "bg-white text-knapp-ink border border-knapp-borderInput hover:border-knapp-ink")
                }
              >
                {cat.label}
              </button>
            );
          })}
        </div>
      </div>

      {(() => {
        if (!Array.isArray(items)) {
          return (
            <p className="text-center mt-24 text-knapp-ink3">
              Produkte konnten nicht geladen werden. Bitte später erneut
              versuchen.
            </p>
          );
        }

        const visibleItems = items
          .filter((item) => item && item.Bild && item.Name)
          .filter((item) => !selected || item.Kategorie === selected)
          .filter((item) => {
            const q = search.trim().toLowerCase();
            if (!q) return true;
            const name = (item.Name ?? "").toLowerCase();
            const desc = (item.Beschreibung ?? "").toLowerCase();
            return name.includes(q) || desc.includes(q);
          })
          .sort((a, b) => {
            switch (sortBy) {
              case "preis_auf":
                return Number(a.Preis_brutto) - Number(b.Preis_brutto);
              case "preis_ab":
                return Number(b.Preis_brutto) - Number(a.Preis_brutto);
              case "name":
                return (a.Name ?? "").localeCompare(b.Name ?? "", "de");
              default: {
                const sa = a.sort_order ?? 9999;
                const sb = b.sort_order ?? 9999;
                return sa - sb;
              }
            }
          });

        if (visibleItems.length === 0) {
          return (
            <div className="flex flex-col items-center justify-center mt-24 px-6 text-center">
              <p className="kn-h3">Keine Produkte gefunden</p>
              <p className="mt-1 kn-body">
                Passen Sie Ihre Suche oder den Filter an.
              </p>
              {(search || selected) && (
                <button
                  onClick={() => {
                    setSearch("");
                    setSelected("");
                  }}
                  className="kn-btn-primary mt-6 py-2.5"
                >
                  Filter zurücksetzen
                </button>
              )}
            </div>
          );
        }

        return (
          <main className="px-5 sm:px-8 lg:px-12 pt-8 pb-20">
            {/* Ergebnisanzahl + Sortierung */}
            <div className="flex flex-wrap items-center gap-3 justify-between mb-5 text-sm text-knapp-ink3">
              <div>
                <strong className="text-knapp-ink">
                  {visibleItems.length} Produkte
                </strong>{" "}
                · {activeCategoryLabel}
              </div>
              <label className="flex items-center gap-2">
                Sortieren
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sortierung"
                  className="font-barlow text-sm text-knapp-ink px-3.5 py-2 border border-knapp-borderInput rounded-full bg-white cursor-pointer outline-none"
                >
                  <option value="standard">Empfohlen</option>
                  <option value="preis_auf">Preis aufsteigend</option>
                  <option value="preis_ab">Preis absteigend</option>
                  <option value="name">Name A–Z</option>
                </select>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 justify-items-center">
              {visibleItems.map((item, i) => (
                <GeneralProductCard
                  key={item.idProdukt}
                  item={item}
                  onClick={handleClick}
                  onAddToCart={() => handleAddToCart(item)}
                  delay={i * 0.05}
                />
              ))}
            </div>
          </main>
        );
      })()}
    </div>
  );
}
